import { useEffect, useCallback, RefObject, useState } from 'react';
import debounce from 'lodash/debounce';

interface ExclusionEventProps {
    ref: RefObject<HTMLElement>;
    callback: () => void;
    eventTypes: (keyof GlobalEventHandlersEventMap)[];
    debounceDelay?: number; // Optional debounce delay in milliseconds
}

/**
 * Hook that fires a callback when a specific event occurs exclusively outside the passed ref
 */
export const useExclusionEvent = ({
    ref,
    callback,
    eventTypes,
    debounceDelay = 250
}: ExclusionEventProps) => {
    const [isActive, setIsActive] = useState(true);

    const startListening = useCallback(() => {
        setIsActive(true);
    }, []);

    const stopListening = useCallback(() => {
        setIsActive(false);
    }, []);

    useEffect(() => {
        const debouncedCallback = debounce(callback, debounceDelay);

        function handleEvent(event: Event) {
            if (
                ref.current &&
                event.target instanceof Node &&
                !ref.current.contains(event.target)
            ) {
                debouncedCallback();
            }
        }

        if (isActive) {
            // Bind the event listeners for all event types
            eventTypes.forEach((type) => {
                document.addEventListener(type, handleEvent);
            });
        } else {
            // Unbind the event listeners
            eventTypes.forEach((type) => {
                document.removeEventListener(type, handleEvent);
            });
        }

        return () => {
            // Clean up: Ensure all event listeners are unbound
            eventTypes.forEach((type) => {
                document.removeEventListener(type, handleEvent);
            });
            debouncedCallback.cancel(); // Important: Cancel any queued execution of debounced callback
        };
    }, [ref, callback, eventTypes, debounceDelay, isActive]);

    return { startListening, stopListening };
};
